import { useMemo } from 'react'
import { Box } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useListPackagingItemsOverview,
  type PackagingItemAuditStatus,
  type PackagingItemComplianceRedFlag,
  type PackagingItemHeavyMetalsAuditFilterValue,
  type PackagingItemSvhcAuditFilterValue,
  type PackagingItemsOverviewSortBy,
  type PackagingItemsOverviewSortDir,
} from '@/shared/hooks/api/packagingItemsOverview'
import { useSearchParamsNavigate } from '@/shared/hooks/use-search-params-navigate'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { PackagingItemsOverviewTable } from './PackagingItemsOverviewTable'
import { PackagingItemsStatsRow } from './PackagingItemsStatsRow'
import { PackagingSuppliersStatCardSkeleton } from './PackagingSuppliersStatCardSkeleton'
import { parsePackagingItemsLastUpdatedFilter } from '@/shared/packagingItemsLastUpdatedFilter'
import {
  PACKAGING_ITEMS_OVERVIEW_HEAVY_METALS_AUDIT_FILTER_VALUES,
} from './packagingItemHeavyMetalsAuditFilter'
import { PACKAGING_ITEMS_OVERVIEW_SVHC_AUDIT_FILTER_VALUES } from './packagingItemSvhcAuditFilter'

const PACKAGING_ITEMS_STAT_CARD_COUNT = 3

const PACKAGING_ITEMS_SORT_BY_VALUES = [
  'name',
  'sku',
  'supplier',
  'status',
  'complianceRedFlags',
  'audit',
  'lastUpdatedByUser',
  'lastUpdatedByLex',
  'heavyMetalsAudit',
  'svhcAudit',
] as const satisfies readonly PackagingItemsOverviewSortBy[]

function parsePositiveInt(value: unknown, fallback: number): number {
  const parsed = Number.parseInt(String(value ?? ''), 10)
  if (Number.isNaN(parsed) || parsed < 1) {
    return fallback
  }
  return parsed
}

function parseStringArrayParam<T extends string>(
  value: unknown,
  allowed: readonly T[]
): T[] | undefined {
  const rawValues = Array.isArray(value)
    ? value
    : typeof value === 'string' && value.trim() !== ''
      ? value.split(',')
      : []
  const parsed = rawValues
    .map((entry) => String(entry).trim())
    .filter((entry): entry is T => allowed.includes(entry as T))
  return parsed.length > 0 ? parsed : undefined
}

function parseSortBy(value: unknown): PackagingItemsOverviewSortBy {
  const raw = typeof value === 'string' ? value.trim() : ''
  if (
    PACKAGING_ITEMS_SORT_BY_VALUES.includes(
      raw as PackagingItemsOverviewSortBy
    )
  ) {
    return raw as PackagingItemsOverviewSortBy
  }
  return 'name'
}

function parseSortDir(value: unknown): PackagingItemsOverviewSortDir {
  return value === 'desc' ? 'desc' : 'asc'
}

export function PackagingItemsPage() {
  const { t } = useTranslation('packagingSuppliers')
  const { search, navigate: searchNavigate } = useSearchParamsNavigate()

  const query = useMemo(() => {
    const page = parsePositiveInt(search.page, 1)
    const pageSize = Math.min(parsePositiveInt(search.pageSize, 20), 50)
    const searchQuery =
      typeof search.q === 'string' ? search.q.trim() : ''
    const supplierId =
      typeof search.supplier === 'string' ? search.supplier.trim() : ''
    const lastUpdated = parsePackagingItemsLastUpdatedFilter(search.lastUpdated)
    const sortBy = parseSortBy(search.sortBy)
    const sortDir = parseSortDir(search.sortDir)

    return {
      page,
      pageSize,
      searchQuery: searchQuery === '' ? undefined : searchQuery,
      supplierId: supplierId === '' ? undefined : supplierId,
      consolidationFilter: 'not_consolidated' as const,
      complianceRedFlags: parseStringArrayParam<PackagingItemComplianceRedFlag>(
        search.complianceRedFlags,
        ['HEAVY_METALS', 'SUBSTANCES_OF_CONCERN']
      ),
      audit: parseStringArrayParam<PackagingItemAuditStatus>(search.audit, [
        'not_audited',
        'clear',
        'flagged',
      ]),
      heavyMetalsAudit: parseStringArrayParam<PackagingItemHeavyMetalsAuditFilterValue>(
        search.heavyMetalsAudit,
        PACKAGING_ITEMS_OVERVIEW_HEAVY_METALS_AUDIT_FILTER_VALUES
      ),
      svhcAudit: parseStringArrayParam<PackagingItemSvhcAuditFilterValue>(
        search.svhcAudit,
        PACKAGING_ITEMS_OVERVIEW_SVHC_AUDIT_FILTER_VALUES
      ),
      lastUpdated,
      sortBy,
      sortDir,
    }
  }, [search])

  const exportQuery = useMemo(() => {
    const { page: _page, pageSize: _pageSize, ...filters } = query
    return filters
  }, [query])

  const { data: packagingItemsOverview, isLoading, isFetching } =
    useListPackagingItemsOverview(query)

  const items = packagingItemsOverview?.items ?? []
  const stats = packagingItemsOverview?.stats
  const pageInfo = packagingItemsOverview?.pageInfo

  const totalItems = stats?.total ?? pageInfo?.totalCount ?? 0
  const showEmptyState = !isLoading && totalItems === 0
  const showStatsSkeleton = !showEmptyState && isLoading
  const showStats = !showEmptyState && !isLoading && stats != null

  return (
    <>
      <Header fixed>
        <Search />
        <div className='ms-auto flex items-center space-x-4'>
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main fixed className='flex min-h-0 flex-1 flex-col gap-4 px-6 sm:gap-6'>
        <div className='shrink-0'>
          <h2 className='text-2xl font-bold tracking-tight'>
            {t('pages.packagingItems.title')}
          </h2>
          <p className='text-muted-foreground'>
            {t('pages.packagingItems.description')}
          </p>
        </div>

        {showEmptyState && (
          <div className='flex flex-1 flex-col items-center justify-center gap-4 rounded-md border border-dashed py-16'>
            <Box className='size-10 text-muted-foreground/50' />
            <div className='space-y-1 text-center'>
              <h3 className='text-lg font-semibold'>
                {t('packagingItems.emptyState.title')}
              </h3>
              <p className='text-sm text-muted-foreground'>
                {t('packagingItems.emptyState.description')}
              </p>
            </div>
          </div>
        )}

        {!showEmptyState && (
          <div className='flex min-h-0 flex-1 flex-col gap-4'>
            {showStatsSkeleton && (
              <div
                className='grid shrink-0 gap-2 sm:grid-cols-2 lg:grid-cols-3'
                aria-busy='true'
                aria-label={t('packagingItems.loading')}
              >
                {Array.from(
                  { length: PACKAGING_ITEMS_STAT_CARD_COUNT },
                  (_, index) => (
                    <PackagingSuppliersStatCardSkeleton key={index} />
                  )
                )}
              </div>
            )}

            {showStats && stats != null && (
              <PackagingItemsStatsRow stats={stats} />
            )}

            {isLoading && items.length === 0 && (
              <div className='shrink-0 py-12 text-center text-muted-foreground'>
                {t('packagingItems.loading')}
              </div>
            )}

            {(items.length > 0 || !isLoading) && pageInfo != null && (
              <PackagingItemsOverviewTable
                data={items}
                pageInfo={pageInfo}
                exportQuery={exportQuery}
                isFetching={isFetching}
                search={search}
                navigate={searchNavigate}
                sortBy={query.sortBy}
                sortDir={query.sortDir}
              />
            )}
          </div>
        )}
      </Main>
    </>
  )
}
